import React, { useState, useEffect, useCallback } from 'react';
import {
  Autocomplete, Box, Card, CardContent, Typography, IconButton, TextField,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent,
  DialogActions, Snackbar, Alert, CircularProgress, Button, Chip,
  Grid, Stack, FormControlLabel, Switch, useTheme, useMediaQuery
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Refresh as RefreshIcon, FilterAltOff as ClearIcon } from '@mui/icons-material';
import { reportCardAPI } from '../../services/api';
import ModulePageHeader from '../common/ModulePageHeader';
import ResponsiveRecordList, { EmptyRecordsState } from '../common/ResponsiveRecordList';
import TablePagination from '../common/TablePagination';
import { useAccessControl, RESOURCES, ACTIONS } from '../../hooks/useAccessControl';

const PAPER_TYPE_OPTIONS = ['Theory', 'Practical', 'Coursework', 'Project', 'Oral', 'Written'];
const STATUS_OPTIONS = [
  { value: '1', label: 'Active' },
  { value: '0', label: 'Inactive' }
];

const responsiveAutocompleteSx = {
  width: '100%',
  minWidth: {
    sm: 240,
    lg: 280
  }
};

const SubjectPapers = () => {
  const { can } = useAccessControl();
  const canRead = can(RESOURCES.REPORT_CARD.SUBJECT_PAPERS, ACTIONS.READ);
  const canCreate = can(RESOURCES.REPORT_CARD.SUBJECT_PAPERS, ACTIONS.CREATE);
  const canUpdate = can(RESOURCES.REPORT_CARD.SUBJECT_PAPERS, ACTIONS.UPDATE);
  const canDelete = can(RESOURCES.REPORT_CARD.SUBJECT_PAPERS, ACTIONS.DELETE);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [papers, setPapers] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, per_page: 15, total: 0 });
  const [subjects, setSubjects] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentPaper, setCurrentPaper] = useState(null);
  const [formData, setFormData] = useState({ subject_id: '', paper_code: '', paper_name: '', paper_number: '', paper_type: 'Theory', description: '', is_active: true });
  const [filters, setFilters] = useState({ subject_id: '', paper_type: '', is_active: '' });
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [dialogLoading, setDialogLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });

  const fetchPapers = useCallback(async (page = 1, perPage = null) => {
    try {
      const params = { page, per_page: perPage || pagination.per_page };
      if (filters.subject_id) params.subject_id = filters.subject_id;
      if (filters.paper_type) params.paper_type = filters.paper_type;
      if (filters.is_active !== '') params.is_active = filters.is_active;
      const res = await reportCardAPI.getSubjectPapers(params);
      if (res.data?.data) {
        setPapers(res.data.data);
        setPagination({
          current_page: res.data.current_page,
          last_page: res.data.last_page,
          per_page: res.data.per_page,
          total: res.data.total,
        });
      } else {
        setPapers(Array.isArray(res.data) ? res.data : []);
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Error fetching papers', severity: 'error' });
    }
  }, [filters, pagination.per_page]);

  const fetchData = useCallback(async () => {
    if (!canRead) return;
    setLoading(true);
    try {
      const subjectsRes = await reportCardAPI.getActiveSubjects();
      setSubjects(Array.isArray(subjectsRes.data) ? subjectsRes.data : subjectsRes.data.data || []);
      await fetchPapers();
    } catch (error) {
      setSnackbar({ open: true, message: 'Error fetching data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [fetchPapers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchPapers();
  }, [fetchPapers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setDialogLoading(true);
    setErrors({});
    try {
      if (editMode) {
        await reportCardAPI.updateSubjectPaper(currentPaper.id, formData);
        setSnackbar({ open: true, message: 'Paper updated successfully!', severity: 'success' });
      } else {
        await reportCardAPI.storeSubjectPaper(formData);
        setSnackbar({ open: true, message: 'Paper added successfully!', severity: 'success' });
      }
      setShowModal(false);
      resetForm();
      fetchPapers();
    } catch (error) {
      if (error.response?.status === 422 && error.response?.data?.errors) {
        setErrors(error.response.data.errors);
        setSnackbar({ open: true, message: 'Please fix the validation errors', severity: 'error' });
      } else {
        setSnackbar({ open: true, message: error.response?.data?.message || 'Error saving paper', severity: 'error' });
      }
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await reportCardAPI.deleteSubjectPaper(deleteDialog.id);
      setSnackbar({ open: true, message: 'Paper deleted successfully!', severity: 'success' });
      setDeleteDialog({ open: false, id: null });
      fetchPapers();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Error deleting paper', severity: 'error' });
    }
  };

  const openModal = (paper = null) => {
    if (paper) {
      setEditMode(true);
      setCurrentPaper(paper);
      setFormData({ subject_id: paper.subject_id, paper_code: paper.paper_code, paper_name: paper.paper_name, paper_number: paper.paper_number, paper_type: paper.paper_type, description: paper.description || '', is_active: paper.is_active });
    } else {
      setEditMode(false);
      setCurrentPaper(null);
      setFormData({ subject_id: '', paper_code: '', paper_name: '', paper_number: '', paper_type: 'Theory', description: '', is_active: true });
    }
    setErrors({});
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({ subject_id: '', paper_code: '', paper_name: '', paper_number: '', paper_type: 'Theory', description: '', is_active: true });
    setEditMode(false);
    setCurrentPaper(null);
  };

  const clearFilters = () => {
    setFilters({ subject_id: '', paper_type: '', is_active: '' });
  };

  if (!canRead) return <Alert severity="error">You do not have permission to view subject papers.</Alert>;

  return (
    <Box>
      <ModulePageHeader
        title="Subject Papers Management"
        subtitle="Manage subject papers"
        actions={[
          <Button key="clear" variant="outlined" startIcon={<ClearIcon />} onClick={clearFilters} fullWidth={isMobile}>Clear Filters</Button>,
          <Button key="refresh" variant="outlined" startIcon={<RefreshIcon />} onClick={fetchPapers} disabled={loading} fullWidth={isMobile}>Refresh</Button>,
          ...(canCreate ? [<Button key="add" variant="contained" startIcon={<AddIcon />} onClick={() => openModal()} fullWidth={isMobile}>Add Paper</Button>] : [])
        ]}
      />

      <Card elevation={3}>
        <CardContent>
          <Box mb={2}>
            <Typography variant="h6" mb={1}>Papers</Typography>
            <Chip label={papers.length} size="small" color="primary" />
          </Box>

          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} md={4}>
              <Autocomplete
                fullWidth
                sx={responsiveAutocompleteSx}
                size="small"
                options={subjects}
                value={subjects.find((subj) => Number(subj.id) === Number(filters.subject_id)) || null}
                onChange={(_, value) => setFilters({ ...filters, subject_id: value?.id || '' })}
                getOptionLabel={(option) => option.subject_name || option.name || 'Subject'}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => <TextField {...params} label="Filter by Subject" />}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                fullWidth
                sx={responsiveAutocompleteSx}
                size="small"
                options={PAPER_TYPE_OPTIONS}
                value={filters.paper_type || null}
                onChange={(_, value) => setFilters({ ...filters, paper_type: value || '' })}
                renderInput={(params) => <TextField {...params} label="Paper Type" />}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                fullWidth
                sx={responsiveAutocompleteSx}
                size="small"
                options={STATUS_OPTIONS}
                value={STATUS_OPTIONS.find((option) => option.value === String(filters.is_active)) || null}
                onChange={(_, value) => setFilters({ ...filters, is_active: value?.value || '' })}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderInput={(params) => <TextField {...params} label="Status" />}
              />
            </Grid>
          </Grid>

          {loading ? (
            <Box py={6} textAlign="center">
              <CircularProgress />
            </Box>
          ) : papers.length === 0 ? (
            <EmptyRecordsState message="No papers found. Click Add Paper to create one." />
          ) : isMobile ? (
            <ResponsiveRecordList
              items={papers}
              renderItem={(paper) => (
                <Stack spacing={1.25}>
                  <Typography fontWeight={700}>{paper.subject?.subject_name || '-'}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {paper.paper_code} | {paper.paper_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Paper #{paper.paper_number} | {paper.paper_type}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {paper.description || 'No description'}
                  </Typography>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Chip label={paper.is_active ? 'Active' : 'Inactive'} color={paper.is_active ? 'success' : 'default'} size="small" />
                    <Box>
                      {canUpdate && <IconButton size="small" onClick={() => openModal(paper)} color="primary"><EditIcon fontSize="small" /></IconButton>}
                      {canDelete && <IconButton size="small" onClick={() => setDeleteDialog({ open: true, id: paper.id })} color="error"><DeleteIcon fontSize="small" /></IconButton>}
                    </Box>
                  </Box>
                </Stack>
              )}
            />
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 2, maxHeight: 600, overflowX: 'auto' }}>
              <Table stickyHeader sx={{ minWidth: 980 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Subject</TableCell>
                    <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Paper Code</TableCell>
                    <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Paper Name</TableCell>
                    <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Paper Number</TableCell>
                    <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {papers.map((paper) => (
                    <TableRow key={paper.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{paper.subject?.subject_name || '-'}</TableCell>
                      <TableCell>{paper.paper_code}</TableCell>
                      <TableCell>{paper.paper_name}</TableCell>
                      <TableCell>{paper.paper_number}</TableCell>
                      <TableCell><Chip label={paper.paper_type} size="small" /></TableCell>
                      <TableCell>{paper.description || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={paper.is_active ? 'Active' : 'Inactive'}
                          color={paper.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {canUpdate && <IconButton size="small" onClick={() => openModal(paper)} color="primary"><EditIcon fontSize="small" /></IconButton>}
                        {canDelete && <IconButton size="small" onClick={() => setDeleteDialog({ open: true, id: paper.id })} color="error"><DeleteIcon fontSize="small" /></IconButton>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            pagination={pagination}
            onPageChange={(page) => fetchPapers(page)}
            onPageSizeChange={(newPerPage) => fetchPapers(1, newPerPage)}
            isMobile={isMobile}
          />
        </CardContent>
      </Card>

      <Dialog open={showModal} onClose={() => setShowModal(false)} fullWidth maxWidth="sm" fullScreen={isMobile}>
        <DialogTitle>{editMode ? 'Edit Subject Paper' : 'Add New Subject Paper'}</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <Autocomplete
              fullWidth
              sx={responsiveAutocompleteSx}
              options={subjects}
              value={subjects.find((subject) => Number(subject.id) === Number(formData.subject_id)) || null}
              onChange={(_, value) => setFormData({ ...formData, subject_id: value?.id || '' })}
              getOptionLabel={(option) => option.subject_name || option.name || 'Subject'}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="normal"
                  label="Subject"
                  required
                  error={!!errors.subject_id}
                  helperText={errors.subject_id?.[0]}
                />
              )}
            />
            <TextField
              label="Paper Code"
              value={formData.paper_code}
              onChange={e => setFormData({ ...formData, paper_code: e.target.value })}
              fullWidth
              margin="normal"
              required
              error={!!errors.paper_code}
              helperText={errors.paper_code?.[0]}
            />
            <TextField
              label="Paper Name"
              value={formData.paper_name}
              onChange={e => setFormData({ ...formData, paper_name: e.target.value })}
              fullWidth
              margin="normal"
              required
              error={!!errors.paper_name}
              helperText={errors.paper_name?.[0]}
            />
            <TextField
              label="Paper Number"
              type="number"
              value={formData.paper_number}
              onChange={e => setFormData({ ...formData, paper_number: e.target.value })}
              fullWidth
              margin="normal"
              required
              error={!!errors.paper_number}
              helperText={errors.paper_number?.[0]}
            />
            <Autocomplete
              fullWidth
              sx={responsiveAutocompleteSx}
              options={PAPER_TYPE_OPTIONS}
              value={formData.paper_type || null}
              onChange={(_, value) => setFormData({ ...formData, paper_type: value || '' })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="normal"
                  label="Paper Type"
                  required
                  error={!!errors.paper_type}
                  helperText={errors.paper_type?.[0]}
                />
              )}
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              margin="normal"
              multiline
              rows={2}
              error={!!errors.description}
              helperText={errors.description?.[0]}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowModal(false)} disabled={dialogLoading}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={dialogLoading}
            startIcon={dialogLoading ? <CircularProgress size={20} /> : null}
          >
            {dialogLoading ? 'Saving...' : editMode ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this paper? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubjectPapers;
